import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Filter,
  Eye,
  PenSquare,
  User,
  Phone,
  ChevronRight,
  Plus,
  FileText,
  BookOpen,
  Truck,
  Loader2,
  AlertTriangle
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { vendorApi } from '../services/api';
import Pagination from '../components/Pagination';
import GroupBillingIcon from '../assets/Group billing.svg';
import GroupRupeerIcon from '../assets/Group rupeer.svg';

function B2BPartners() {
  const { currentTheme } = useTheme();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Backend vendor object → UI card shape
  const initials = (name = '') => name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase() || 'V';
  const mapVendor = (v) => ({
    id: v.id,
    companyName: v.companyName || '',
    contactPerson: v.contactPersonName || '',
    contactNumber: v.contactNumber || '',
    gstin: v.gstin || '',
    drugLicense1: v.drugLicensePart1 || '',
    drugLicense2: v.drugLicensePart2 || '',
    outstandingBills: v.purchasedBillsTotal ?? 0,
    orders: v.ordersCount ?? 0,
    status: v.active === false ? 'inactive' : 'active',
    avatarInitials: initials(v.companyName),
  });

  const loadVendors = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = debouncedSearch
        ? await vendorApi.searchVendors(debouncedSearch)
        : await vendorApi.searchVendors('');
      const list = res?.content || res?.data || res || [];
      setPartners((Array.isArray(list) ? list : []).map(mapVendor));
    } catch (err) {
      setError(err.message || 'Failed to load vendors');
      console.error('Vendors load error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVendors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  // ── Toggle active/inactive (API) ──
  const togglePartnerStatus = async (id) => {
    // Optimistic UI update
    setPartners(prev => prev.map(p => p.id === id ? { ...p, status: p.status === 'active' ? 'inactive' : 'active' } : p));
    try {
      await vendorApi.toggleVendorActive(id);
    } catch (err) {
      // Revert on failure
      setPartners(prev => prev.map(p => p.id === id ? { ...p, status: p.status === 'active' ? 'inactive' : 'active' } : p));
      alert(err.message || 'Failed to update vendor status');
    }
  };

  const filteredPartners = partners; // search already applied server-side

  return (
    <div className="space-y-6">
      {/* Header & Search Bar */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-0">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:space-x-8 w-full md:w-auto">
          <h1 className="text-2xl font-bold text-gray-900 border-l-4 pl-4 shrink-0" style={{ borderColor: currentTheme.color }}>Vendors</h1>

          {/* Search bar matching screenshot */}
          <div className="w-full sm:w-[320px] relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by GSTIN number or company name"
              className="w-full pl-10 pr-4 py-2.5 text-xs rounded-full border border-gray-200 outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all text-gray-700 placeholder-gray-400 shadow-sm bg-white"
            />
          </div>
        </div>

        {/* Filter and Add buttons */}
        <div className="flex items-center gap-3 shrink-0">
          <button className="p-2.5 bg-white border border-gray-250 rounded-full hover:bg-gray-150 shadow-sm transition-all text-gray-700 shrink-0">
            <Filter className="w-4.5 h-4.5" />
          </button>
          <button
            onClick={() => navigate('/b2b-partners/add')}
            className="p-2.5 bg-white border border-gray-250 rounded-full hover:bg-gray-150 shadow-sm transition-all text-gray-700 shrink-0"
          >
            <div className="relative w-4.5 h-4.5 flex items-center justify-center">
              <Truck className="w-4 h-4" />
              <span className="absolute -top-1.5 -right-1.5 bg-emerald-500 text-white rounded-full w-3.5 h-3.5 flex items-center justify-center text-[9px] font-black border border-white shadow-sm">+</span>
            </div>
          </button>
        </div>
      </div>

      {/* ── ERROR BANNER ── */}
      {error && (
        <div className="flex items-center gap-3 p-4 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 text-sm font-semibold">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={loadVendors} className="ml-auto text-xs font-black uppercase tracking-wider underline cursor-pointer">
            Retry
          </button>
        </div>
      )}

      {/* Partners Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-emerald-500 mr-3" />
          <span className="text-gray-400 font-semibold">Loading vendors...</span>
        </div>
      ) : filteredPartners.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <p className="text-sm font-bold text-gray-500">No vendors found.</p>
          {debouncedSearch && <p className="text-xs text-gray-400 mt-1">Try a different search term.</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPartners
            .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
            .map((partner) => (
              <div key={partner.id} className="bg-white rounded-2xl border border-gray-200/80 shadow-md hover:shadow-lg transition-all p-5 flex flex-col justify-between">
                <div>
                  {/* Card Top: Profile and Buttons */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-gray-200 bg-emerald-50 flex items-center justify-center text-emerald-700 font-black text-lg">
                        {partner.avatarInitials}
                      </div>
                      <div>
                        <h3 className="font-extrabold text-base text-gray-900 leading-tight">{partner.companyName}</h3>
                        <div className="flex items-center space-x-1.5 text-xs text-gray-500 font-semibold mt-1">
                          <User className="w-3.5 h-3.5 text-gray-400" />
                          <span>{partner.contactPerson}</span>
                        </div>
                        <div className="flex items-center space-x-1.5 text-xs text-gray-500 font-semibold mt-0.5">
                          <Phone className="w-3.5 h-3.5 text-gray-400" />
                          <span>{partner.contactNumber}</span>
                        </div>
                      </div>
                    </div>

                    {/* Top-Right: Actions Buttons */}
                    <div className="flex space-x-1">
                      {/* Eye Button */}
                      <button
                        onClick={() => navigate(`/b2b-partners/${partner.id}`)}
                        className="w-7 h-7 flex items-center justify-center bg-[#00A5B5] hover:bg-[#00818E] text-white rounded-lg transition-all shadow-sm"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>

                      {/* Document Button */}
                      <button
                        onClick={() => navigate(`/b2b-partners/${partner.id}/document`)}
                        className="w-8 h-8 flex items-center justify-center hover:scale-110 transition-all shadow-sm"
                        title="Documents"
                      >
                        <img src={GroupRupeerIcon} alt="Documents" className="w-8 h-8" />
                      </button>

                      {/* Invoice/Ledger Button */}
                      <button
                        onClick={() => navigate(`/b2b-partners/${partner.id}/invoice`)}
                        className="w-8 h-8 flex items-center justify-center hover:scale-110 transition-all shadow-sm"
                        title="Invoices"
                      >
                        <img src={GroupBillingIcon} alt="Invoices" className="w-8 h-8" />
                      </button>

                      {/* Edit Button */}
                      <button
                        onClick={() => navigate(`/b2b-partners/${partner.id}/edit`)}
                        className="w-7 h-7 flex items-center justify-center bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-lg transition-all shadow-sm"
                        title="Edit"
                      >
                        <PenSquare className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Switch Toggle Status under buttons */}
                  <div className="flex justify-end mt-2 items-center">
                    <div
                      onClick={() => togglePartnerStatus(partner.id)}
                      className={`w-9 h-5 rounded-full relative cursor-pointer transition-all duration-300 ${partner.status === 'active' ? 'bg-emerald-500/20 border-emerald-500/30 border' : 'bg-gray-250 border-gray-300 border'}`}
                    >
                      <div className={`absolute top-0.5 w-3.5 h-3.5 rounded-full transition-all duration-300 shadow-sm ${partner.status === 'active' ? 'right-0.5 bg-emerald-500' : 'left-0.5 bg-gray-400'}`}></div>
                    </div>
                  </div>

                  {/* Registration Info */}
                  <div className="mt-4 space-y-1.5">
                    <p className="text-xs font-semibold text-gray-500">Registration</p>
                    <div className="bg-[#F0FDFA] rounded-xl p-4 border border-[#CCFBF1]">
                      <div className="flex items-center space-x-2 mb-2.5">
                        <span className="text-xs text-gray-400">GSTIN:</span>
                        <span className="text-xs font-bold text-gray-700">{partner.gstin}</span>
                      </div>
                      <div className="h-[1px] bg-[#CCFBF1] w-full mb-2.5"></div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider">Drug Licence: Part 1</p>
                          <p className="text-xs font-bold text-gray-700 mt-0.5">{partner.drugLicense1}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider">Drug Licence: Part 2</p>
                          <p className="text-xs font-bold text-gray-700 mt-0.5">{partner.drugLicense2}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bottom statistics: Purchased bills and Orders */}
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <div className="bg-[#FFF9F2] rounded-xl p-3 border border-[#FFE8CC] flex flex-col justify-between">
                    <p className="text-xs font-semibold text-[#EA580C]">Purchased bills:</p>
                    <p className="text-xl font-bold text-[#EA580C] mt-1">₹ {partner.outstandingBills.toLocaleString()}</p>
                  </div>
                  <div className="bg-[#E6FFFA] rounded-xl p-3 border border-[#B2F5EA] relative flex flex-col justify-between group">
                    <div>
                      <p className="text-xs font-semibold text-[#0D9488]">Orders</p>
                      <p className="text-xl font-bold text-[#0D9488] mt-1">{partner.orders}</p>
                    </div>
                    <div
                      onClick={() => navigate(`/b2b-partners/${partner.id}`)}
                      className="absolute bottom-3 right-3 w-6 h-6 bg-[#0D9488] rounded-full flex items-center justify-center text-white cursor-pointer transition-all hover:bg-[#115E59] shadow-sm hover:scale-105"
                    >
                      <ChevronRight className="w-3.5 h-3.5 pointer-events-none" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && filteredPartners.length > 0 && (
        <div className="mt-4">
          <Pagination
            currentPage={currentPage}
            totalItems={filteredPartners.length}
            itemsPerPage={itemsPerPage}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      )}
    </div>
  );
}

export default B2BPartners;